import { useEffect, useState } from 'react';
import { BodyText, Button, Div, GrowlNotification, Gutter, ModalDialog } from '@gaiads/telia-react-component-library';
import { useHistory, useParams } from 'react-router-dom';
import { PageHeader } from '@cdaas/reusable-components-daas';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  InvoicingAccountOptionResponseContents,
  OrganizationWithUserGroupsResponse,
  UserGroupRequest,
  UserGroupResponse,
} from '@cdaas/api-clients/dist/asset-admin-openapi';

import { routes, withCustomerOrgFormPath } from '../../App/routes';
import InvoicingAccounts from '../../../Components/InvoicingAccounts/InvoicingAccounts';
import PurchaseLimits from './PurchaseLimits';
import { deleteAllDefaultAddressesFromUserGroupRequestIfEmpty, getUserGroupRequestToUpdateExistingUserGroup } from '../../../utils';
import Message from '../../App/Message';
import { Loader } from '../../App/Loader';
import UserGroupUsers from './UserGroupUsers';
import { UserPermissions } from './UserPermissions';
import { BasicInformation } from './BasicInformation';
import { DetailsPageMainContent, DetailsPageMainContentLeftSide } from '../../App/Layouts/DetailsPageLayout';
import { assetAdminInvoicingAccountClient, assetAdminOrganizationClient, assetAdminUserGroupClient } from '../../../api-client/asset-admin-client';

interface UserGroupParam {
  id: string;
  usergroupid: string;
}

const UserGroupDetails = () => {
  const [loading, setLoading] = useState(true);
  const [userGroup, setUserGroup] = useState<UserGroupResponse>();
  const [organization, setOrganization] = useState<OrganizationWithUserGroupsResponse>();
  const [invoicingAccounts, setInvoicingAccounts] = useState<InvoicingAccountOptionResponseContents[]>([]);
  const [apiError, setApiError] = useState(false);
  const [editMode, setEditMode] = useState<null | 'infoCard' | 'purchaseLimitsCard'>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const history = useHistory();
  const { t } = useTranslation('userGroups');
  const userGroupId = useParams<UserGroupParam>().usergroupid;
  const organizationId = useParams<UserGroupParam>().id;

  const fetchUserGroup = async (userGroupId: string, organizationId: string) => {
    setLoading(true);
    try {
      const invoicingAccounts = await assetAdminInvoicingAccountClient.adminReadInvoicingAccountOptions(organizationId, 99999);
      if (invoicingAccounts && invoicingAccounts.data && invoicingAccounts.data.contents.length) {
        setInvoicingAccounts(invoicingAccounts.data.contents);
      }
      // GETing invoicing accounts from asset service makes asset service update invoicing accounts information
      // (e.g. has some invoicing account been removed) from Telia finance.
      // GETing invoicing accounts before GETing usergroup makes sure usergroup's invoicing account has
      // up-to-date info if it's active=true|false
      const responses = await Promise.all([
        assetAdminUserGroupClient.adminUserGroupRead(organizationId, userGroupId),
        assetAdminOrganizationClient.adminOrganizationRead(organizationId),
      ]);
      setUserGroup(responses[0].data);
      setOrganization(responses[1].data);
    } catch (error) {
      console.error(error);
      setApiError(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUserGroup(userGroupId, organizationId);
  }, [userGroupId, organizationId]);

  const handleOnLinkClick = () => {
    if (!organizationId) {
      return;
    }
    history.push(withCustomerOrgFormPath(routes.userGroups, organizationId));
  };

  const updateUserGroup = async (updateData: Partial<UserGroupRequest>): Promise<boolean> => {
    try {
      let userGroupUpdateRequest: UserGroupRequest = {
        ...getUserGroupRequestToUpdateExistingUserGroup(userGroup!),
        ...updateData,
      };
      userGroupUpdateRequest = deleteAllDefaultAddressesFromUserGroupRequestIfEmpty(userGroupUpdateRequest);
      const updatedUserGroup = (await assetAdminUserGroupClient.adminUserGroupUpdate(organizationId, userGroup?.id!, userGroupUpdateRequest)).data;
      setUserGroup(updatedUserGroup);
      setEditMode(null);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const deleteUserGroup = async () => {
    if (!userGroup?.id) {
      return;
    }
    setDeleteError('');
    try {
      await assetAdminUserGroupClient.adminUserGroupDelete(organizationId, userGroup.id);
      history.push(withCustomerOrgFormPath(routes.userGroups, organizationId));
    } catch (err) {
      console.error(err);
      setDeleteError(
        axios.isAxiosError(err) && err.response?.data?.message
          ? `${t('userGroupDetails.deleteUserGroupApiErrorPrefix')} ${err.response.data.message}`
          : t('userGroupDetails.deleteUserGroupGenericError'),
      );
      setShowDeleteModal(false);
    }
  };

  const deleteUserGroupModal = (
    <ModalDialog
      destructive
      isOpen={showDeleteModal}
      title={t('userGroupDetails.deleteUserGroupModal.title')}
      acceptButtonText={t('userGroupDetails.deleteUserGroupModal.deleteButton')}
      closeButtonText={t('userGroupDetails.deleteUserGroupModal.cancelButton')}
      onCloseClick={() => setShowDeleteModal(false)}
      onAcceptClick={deleteUserGroup}
    >
      <BodyText>{t('userGroupDetails.deleteUserGroupModal.confirmationText')}</BodyText>
      <Gutter />
      <BodyText>
        {t('userGroupDetails.deleteUserGroupModal.name', {
          name: userGroup?.name,
          interpolation: { escapeValue: false },
        })}
      </BodyText>
    </ModalDialog>
  );

  const render = () => {
    const topContent = (
      <Div padding={{ vertical: 'md' }}>
        <PageHeader linkText={t('userGroupDetails.backToLink')} onLinkClick={handleOnLinkClick}>
          {userGroup?.name}
        </PageHeader>
      </Div>
    );

    const leftSideContent = (
      <>
        {apiError && <Message text={t('userGroupDetails.apiError')} />}
        {loading && <Loader />}

        {!loading && !apiError && (
          <>
            {!!userGroup && (
              <>
                <BasicInformation
                  userGroup={userGroup}
                  onEditButtonClick={() => setEditMode('infoCard')}
                  onSaveButtonClick={updateUserGroup}
                  onCancelButtonClick={() => setEditMode(null)}
                  modalVisible={editMode === 'infoCard'}
                />
                <Gutter />
                {!!organization && (
                  <PurchaseLimits
                    userGroup={userGroup}
                    organization={organization}
                    modalVisible={editMode === 'purchaseLimitsCard'}
                    onClickEdit={() => setEditMode('purchaseLimitsCard')}
                    updateUserGroupPurchaseLimits={updateUserGroup}
                    cancelUpdateUserGroupPurchaseLimits={() => setEditMode(null)}
                  />
                )}
                <Gutter />
                <UserPermissions userGroup={userGroup} updateUserGroupUserPermissions={updateUserGroup} />
                <Gutter />
                <InvoicingAccounts userOrUserGroup={userGroup} invoicingAccounts={invoicingAccounts} updateInvoicingAccount={updateUserGroup} />
                <Gutter size="layout-5" />
                <UserGroupUsers userGroup={userGroup} />
                <Gutter size="xlg" />
                <Button variant="destructive" onClick={() => setShowDeleteModal(true)}>
                  {t('userGroupDetails.deleteButton')}
                </Button>
                <Gutter size="xlg" />
              </>
            )}
          </>
        )}
      </>
    );

    return (
      <>
        {!loading && !apiError && topContent}
        <DetailsPageMainContent>
          <DetailsPageMainContentLeftSide>{leftSideContent}</DetailsPageMainContentLeftSide>
        </DetailsPageMainContent>
        {deleteUserGroupModal}
        {deleteError && <GrowlNotification notification={deleteError} variant={'error'} closeOnClick={() => setDeleteError(null)} />}
      </>
    );
  };

  return render();
};

export default UserGroupDetails;
